import { Fragment, useContext, useEffect, useState } from "react";
import { useServerForm } from "hooks/useServerForm";
import { Textbox } from "components/fields/Textbox";
import { SelectList } from "components/fields/SelectList";
import { IconText } from "components/IconText";
import { GlobalContext } from "contexts/GlobalContext";
import { Country, IState, State } from "country-state-city";
import { IdNameDto } from "dtos/IdNameDto";
import { LanguageDto } from "dtos/LanguageDto";

export interface HallOfFameSearchFormData {
    query?: string;
    category?: number;
    language?: string;
    country?: string;
    region?: string;
    schoolLevel?: number;
}

export const HallOfFameSearchForm = (props: {
    onSubmit: (data: HallOfFameSearchFormData) => void;
    isLoading: boolean;
    countries: Array<string>;
    categories: Array<IdNameDto>;
    languages: Array<LanguageDto>;
    schoolLevels: Array<IdNameDto>;
    defaultValues: Partial<HallOfFameSearchFormData>;
}) => {
    const { onSubmit, isLoading, defaultValues, countries, categories, languages, schoolLevels } = props;

    const { regionNames } = useContext(GlobalContext);

    const [regions, setRegions] = useState<Array<IState>>([]);

    const { register, handleSubmit, errors, reset, watch } = useServerForm<HallOfFameSearchFormData>({
        defaultValues,
    });

    const watchedData = watch();
    const { query, category, language, country, region, schoolLevel } = watchedData;

    const isValidSearch =
        (query && query.trim().length > 0) || category || language || country || region || schoolLevel;

    // Update regions when country changes
    useEffect(() => {
        if (country) {
            const countryData = Country.getCountryByCode(country);

            if (countryData) {
                setRegions(State.getStatesOfCountry(countryData.isoCode));
            }
        } else {
            setRegions([]);
        }
    }, [country]);

    useEffect(() => {
        reset(defaultValues);
    }, [reset, defaultValues]);

    const handleFormSubmit = handleSubmit((data: HallOfFameSearchFormData) => {
        onSubmit(data);
    });

    const countriesOptions = countries
        .map(country => ({
            value: country,
            label: regionNames.of(country) || country,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const regionOptions = regions
        .map(r => ({
            value: r.isoCode,
            label: r.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const categoryOptions = categories
        .map(c => ({
            value: c.id.toString(),
            label: c.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const languageOptions = languages
        .map(l => ({
            value: l.code,
            label: l.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const schoolLevelOptions = schoolLevels.map(sl => ({
        value: sl.id.toString(),
        label: sl.name,
    }));

    return (
        <form onSubmit={handleFormSubmit}>
            <div className="card">
                <div className="card-body">
                    <div className="vstack gap-4">
                        <Textbox
                            register={register}
                            name="query"
                            label="Search"
                            type="search"
                            disabled={isLoading}
                            placeholder="Search games and prompts..."
                            errors={errors}
                        />

                        <SelectList
                            register={register}
                            name="category"
                            label="Category"
                            disabled={isLoading}
                            options={categoryOptions}
                            defaultOptionLabel="All categories"
                            errors={errors}
                        />

                        <SelectList
                            register={register}
                            name="schoolLevel"
                            label="School level"
                            disabled={isLoading}
                            options={schoolLevelOptions}
                            defaultOptionLabel="All school levels"
                            errors={errors}
                        />

                        <SelectList
                            register={register}
                            name="country"
                            label="Country"
                            disabled={isLoading}
                            options={countriesOptions}
                            defaultOptionLabel="All countries"
                            errors={errors}
                        />

                        <SelectList
                            register={register}
                            name="region"
                            disabled={isLoading || !country || regions.length === 0}
                            label="Region"
                            options={regionOptions}
                            defaultOptionLabel="All regions"
                            errors={errors}
                        />

                        <SelectList
                            register={register}
                            name="language"
                            label="Language"
                            disabled={isLoading}
                            options={languageOptions}
                            defaultOptionLabel="All languages"
                            errors={errors}
                        />
                    </div>
                </div>

                <div className="card-footer">
                    <button
                        type="submit"
                        className="btn btn-dark icon-link w-100 justify-content-center"
                        disabled={isLoading || !isValidSearch}
                    >
                        {isLoading ? (
                            <Fragment>
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                                Searching...
                            </Fragment>
                        ) : (
                            <IconText icon="fas fa-search" text="Search games" />
                        )}
                    </button>
                </div>
            </div>
        </form>
    );
};
